import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const services = [
  {
    icon: "ri-newspaper-line",
    title: "最新汽车新闻",
    desc: "覆盖新车上市、产品改款与市场行情，按发布时间快速更新。",
  },
  {
    icon: "ri-speed-up-line",
    title: "专业车型评测",
    desc: "从动力、能耗、空间到智能体验，提供深度试驾与对比分析。",
  },
  {
    icon: "ri-line-chart-line",
    title: "行业动态观察",
    desc: "追踪政策法规、产销数据与技术趋势，理解产业发展逻辑。",
  },
  {
    icon: "ri-search-line",
    title: "便捷分类检索",
    desc: "支持分类筛选与关键词搜索，帮助你快速定位感兴趣的资讯。",
  },
];

const team = [
  { name: "编辑部", role: "内容策划", desc: "负责资讯选题与栏目规划，把握内容方向。" },
  { name: "评测组", role: "深度试驾", desc: "负责车型试驾与评测，提供客观专业的产品分析。" },
  { name: "行业组", role: "趋势研究", desc: "负责行业数据与政策解读，输出趋势观察内容。" },
  { name: "产品组", role: "体验设计", desc: "负责阅读体验与信息结构优化，让查阅更高效。" },
];

export default function About() {
  useDocumentMeta({
    title: "关于我们 - 汽车资讯网",
    description:
      "了解汽车资讯网的定位、服务内容与团队。我们致力于为汽车爱好者、购车用户与行业观察者提供权威、丰富的汽车资讯。",
    keywords: "关于汽车资讯网,汽车资讯平台,汽车媒体,团队介绍",
  });

  return (
    <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb items={[{ label: "关于我们" }]} />

      <div className="mt-4 bg-primary-950 rounded-lg p-6 md:p-12">
        <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-accent-500 text-primary-950 font-semibold mb-4">
          <i className="ri-steering-2-line" />
          ABOUT US
        </span>
        <h1 className="font-heading font-bold text-2xl md:text-4xl text-background-50 max-w-3xl leading-snug">
          做懂车、也懂你的汽车资讯平台
        </h1>
        <p className="mt-4 text-sm md:text-base text-background-200 max-w-3xl leading-relaxed">
          汽车资讯网聚焦汽车行业，围绕最新汽车新闻、车型评测与行业动态，为汽车爱好者、潜在购车用户及行业关注者提供权威、丰富、分类明确的信息服务。我们希望通过清晰的信息结构与舒适的阅读体验，让了解汽车这件事变得简单。
        </p>
      </div>

      <section className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
            网站定位
          </h2>
          <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
            <p>
              我们专注于汽车垂直领域，内容覆盖轿车、SUV、新能源车、性能跑车等多个细分方向。无论是第一次购车，还是持续关注行业动态，你都能在这里找到结构清晰、表达专业的内容。
            </p>
            <p>
              在内容生产上，我们坚持“及时、准确、可读”。及时意味着第一时间捕捉车市变化；准确意味着信息有据可依；可读意味着用通俗的语言把专业内容讲清楚，降低理解门槛。
            </p>
            <p>
              在阅读体验上，我们注重分类与筛选的便捷性，通过频道导航、子分类切换与关键词搜索，帮助你高效地找到所需信息。
            </p>
          </div>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-lg p-6">
          <h3 className="font-heading font-semibold text-lg text-foreground-950 mb-4">
            我们的数据
          </h3>
          <div className="space-y-5">
            {[
              { value: "4", label: "核心细分频道" },
              { value: "48+", label: "演示资讯内容" },
              { value: "3", label: "内容方向：新闻 / 评测 / 行业" },
            ].map((item) => (
              <div key={item.label}>
                <p className="font-heading font-bold text-3xl text-accent-600">
                  {item.value}
                </p>
                <p className="text-sm text-foreground-500 mt-1">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mt-12">
        <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-6">
          服务内容
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((service) => (
            <div
              key={service.title}
              className="bg-background-50 border border-background-200 rounded-lg p-5 hover:border-primary-300 transition-colors"
            >
              <span className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center mb-4">
                <i className={`${service.icon} text-xl text-primary-600`} />
              </span>
              <h3 className="font-heading font-semibold text-base text-foreground-950">
                {service.title}
              </h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                {service.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12">
        <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-6">
          团队介绍
        </h2>
        <p className="text-sm text-foreground-500 mb-6">
          （以下为虚构示范介绍，仅用于展示页面结构）
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {team.map((member) => (
            <div
              key={member.name}
              className="bg-background-50 border border-background-200 rounded-lg p-5 text-center"
            >
              <span className="w-16 h-16 rounded-full bg-secondary-100 text-secondary-800 flex items-center justify-center mx-auto mb-4">
                <i className="ri-team-line text-2xl" />
              </span>
              <h3 className="font-heading font-semibold text-base text-foreground-950">
                {member.name}
              </h3>
              <p className="text-xs text-accent-600 mt-1">{member.role}</p>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                {member.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12 bg-accent-500 rounded-lg p-6 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div>
          <h2 className="font-heading font-bold text-xl md:text-2xl text-primary-950">
            有内容建议或合作意向？
          </h2>
          <p className="mt-2 text-sm text-primary-900 max-w-xl">
            欢迎通过联系页面与我们取得联系，我们会认真对待每一条反馈。
          </p>
        </div>
        <Link
          to="/contact"
          className="h-11 px-6 rounded-md bg-primary-900 text-background-50 text-sm flex items-center gap-1.5 hover:bg-primary-950 transition-colors cursor-pointer whitespace-nowrap w-fit"
        >
          前往联系页面
          <i className="ri-arrow-right-line" />
        </Link>
      </section>
    </div>
  );
}